import { Checkbox, Icon, Loader, Text } from '@scality/core-ui';
import { Input } from '@scality/core-ui/dist/next';
import type { UseComboboxPropGetters } from 'downshift';
import type React from 'react';
import { SelectMenuPlainList, SelectRowCheckbox } from '../../../select';
import {
  CounterText,
  DeploymentMenuItem,
  DeploymentMenuPanel,
  ListScroll,
  SearchField,
  SearchSpinner,
  SearchWrapper,
} from './DeploymentSelector.styles';
import type { DeploymentSelectorLabels } from './deploymentSelectorLabels';
import type { DeploymentItem, SelectorNoun } from './types';

type PropGetters = UseComboboxPropGetters<DeploymentItem>;

export type DeploymentMenuProps = {
  isOpen: boolean;
  /** What to list: the selection when showing it back, else the filtered page. */
  rows: DeploymentItem[];
  /** Rows in hand before filtering, which the counter reports against `totalCount`. */
  itemCount: number;
  totalCount?: number;
  multiple: boolean;
  /** An empty search box lists the selection instead of the fleet (multi-select only). */
  isShowingSelection: boolean;
  selectedCount: number;
  isSelected: (item: DeploymentItem) => boolean;
  canToggle: (item: DeploymentItem) => boolean;
  highlightedIndex: number;
  getItemProps: PropGetters['getItemProps'];
  getInputProps: PropGetters['getInputProps'];
  /** The current search term, so a fruitless one can name itself. */
  searchTerm: string;
  isSearching: boolean;
  noun: SelectorNoun;
  labels: DeploymentSelectorLabels;
  /** The panel's own plumbing: downshift's menu props merged with Floating UI's position. */
  panelRef: React.Ref<HTMLDivElement>;
  panelProps: React.ComponentPropsWithoutRef<'div'>;
  panelStyle: React.CSSProperties;
};

/** The floating panel: a search box over a scrolling list of rows. */
export const DeploymentMenu: React.FC<DeploymentMenuProps> = ({
  isOpen,
  rows,
  itemCount,
  totalCount,
  multiple,
  isShowingSelection,
  selectedCount,
  isSelected,
  canToggle,
  highlightedIndex,
  getItemProps,
  getInputProps,
  searchTerm,
  isSearching,
  noun,
  labels,
  panelRef,
  panelProps,
  panelStyle,
}) => {
  const hasMoreThanShown = typeof totalCount === 'number' && totalCount > itemCount;

  return (
    <DeploymentMenuPanel ref={panelRef} {...panelProps} style={panelStyle} aria-hidden={!isOpen} $isOpen={isOpen}>
      <SearchWrapper>
        <SearchField>
          <Input
            id="deployment-selector-search"
            type="text"
            autoComplete="off"
            noPlaceholderPrefix
            fluid
            placeholder={labels.searchBox}
            {...getInputProps()}
          />
          {isSearching && (
            <SearchSpinner role="status" aria-label={`Searching ${noun.plural}`}>
              <Loader size="small" />
            </SearchSpinner>
          )}
        </SearchField>

        <CounterText variant="Small" color="textSecondary">
          {isShowingSelection
            ? `${labels.nouns} (${selectedCount} selected)`
            : hasMoreThanShown
              ? `${labels.nouns} (${itemCount} of ${totalCount})`
              : `${labels.nouns} (${itemCount})`}
        </CounterText>
        {isShowingSelection ? (
          <CounterText variant="Smaller" color="textSecondary">
            {`Type to search all ${labels.availableCount}`}
          </CounterText>
        ) : labels.selectionTally ? (
          <CounterText variant="Smaller" color="textSecondary">
            {labels.selectionTally}
          </CounterText>
        ) : (
          hasMoreThanShown && (
            <CounterText variant="Smaller" color="textSecondary">
              Refine your search to narrow this list
            </CounterText>
          )
        )}
      </SearchWrapper>
      <ListScroll>
        <SelectMenuPlainList>
          {isOpen &&
            rows.map((item, index) => (
              <DeploymentMenuItem
                key={item.id}
                $highlighted={highlightedIndex === index}
                {...getItemProps({ item, index })}
                $disabled={!canToggle(item)}
                $multi={multiple}
                aria-disabled={!canToggle(item)}
              >
                {multiple && (
                  <SelectRowCheckbox>
                    <Checkbox checked={isSelected(item)} disabled={!canToggle(item)} readOnly tabIndex={-1} />
                  </SelectRowCheckbox>
                )}
                <Text>{item.label}</Text>
                {!multiple && isSelected(item) && <Icon name="Check" size="sm" />}
              </DeploymentMenuItem>
            ))}
          {isOpen && !isShowingSelection && rows.length === 0 && searchTerm && (
            <DeploymentMenuItem $highlighted={false}>
              <Text variant="Basic" color="textSecondary">
                No match for &quot;{searchTerm}&quot;
              </Text>
            </DeploymentMenuItem>
          )}
        </SelectMenuPlainList>
      </ListScroll>
    </DeploymentMenuPanel>
  );
};
